import React, { useState, useEffect, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '@context/AuthContext';
import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  setDoc,
  updateDoc,
  increment,
  query,
  where,
} from 'firebase/firestore';
import { db, storage } from '@root/firebase';
import { ref as deleteRef, deleteObject } from 'firebase/storage';
import Layout from '@components/Layout';
import DocUploader from '@components/DocUploader';
import { toast } from 'react-hot-toast';
import TagInput from '@components/TagInput';
import { v4 as uuidv4 } from 'uuid';
import Tour from '@components/Tour';
import { libraryAdminTourSteps } from '@utils/TourSteps/LibraryAdmin';
import Modal from '@/components/Modal';
import ReadMoreTextBase from '@components/readmore/ReadMoreTextBase';

function LibraryAdmin() {
  const { globalUser } = useAuth();

  // --- state ---
  const [libraries, setLibraries] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteFileName, setDeleteFileName] = useState('');
  const [deleteFileObj, setDeleteFileObj] = useState('');

  const [newLibraryName, setNewLibraryName] = useState('');
  const [newSubLibraryName, setNewSubLibraryName] = useState('');
  const [activeAccordion, setActiveAccordion] = useState(null);

  const [selectedParent, setSelectedParent] = useState('');
  const [selectedChild, setSelectedChild] = useState('');

  const [fileTitle, setFileTitle] = useState('');
  const [docReviewDate, setDocReviewDate] = useState('');
  const [docDeleteDate, setDocDeleteDate] = useState('');
  const [tags, setTags] = useState([]);

  const [recentFiles, setRecentFiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 5;

  const [isUploading, setIsUploading] = useState(false);
  const docUploaderRef = useRef();

  const [isDeleting, setIsDeleting] = useState(false);

  // Build an array of page numbers with smart ellipses
  function getPageNumbers(current, total, maxVisible = 7) {
    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages = [];
    const left = Math.max(2, current - 2);
    const right = Math.min(total - 1, current + 2);

    pages.push(1);
    if (left > 2) pages.push('…');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  }

  // ----------------------------
  // Helpers
  // ----------------------------
  const toggleAccordion = (libraryId) => {
    setActiveAccordion((prev) => (prev === libraryId ? null : libraryId));
  };

  // Return the children of a given parent
  const getChildLibraries = useCallback(
    (parentId) => (libraries ?? []).filter((lib) => lib.parent === parentId),
    [libraries]
  );

  // Simple click handler for child folder chip
  const handleLibraryClick = (subLibraryId, subLibraryName) => {
    // selecting a subfolder should set parent & child properly
    const child = libraries.find((l) => l.id === subLibraryId);
    if (child) {
      setSelectedParent(child.parent || '');
      setSelectedChild(child.id);
    }
    toast.success(`Selected: ${subLibraryName}`);
  };

  // Handles Firestore Timestamp, ISO strings, numbers, or Date objects
  function toDateFromAny(input) {
    if (!input) return null;
    if (input instanceof Date) return input;
    if (typeof input?.toDate === 'function') return input.toDate(); // Firestore Timestamp
    if (typeof input === 'object' && 'seconds' in input) {
      // { seconds, nanoseconds }
      return new Date(input.seconds * 1000);
    }
    return new Date(input); // string | number
  }

  // 27 Sep 2025 (day 2-digit, short month, full year)
  // en-GB gives "27 Sept 2025" on many systems; if you want strictly "Sep", use 'en-US' below.
  function formatShortDate(input) {
    const d = toDateFromAny(input);
    if (!d || isNaN(d)) return 'Unknown';
    // Use 'en-GB' for UK-style order; switch to 'en-US' if you prefer "Sep" instead of "Sept"
    return d
      .toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
      .replace(/\./g, ''); // remove any locale dots after month abbrev
  }

  // Build breadcrumb from a folder id up to root
  const fetchBreadcrumb = useCallback(async (folderId) => {
    const chain = [];
    let current = folderId;
    while (current) {
      const snap = await getDoc(doc(db, 'library', current));
      if (!snap.exists()) break;
      const data = snap.data();
      chain.push({ id: current, name: data.library_name || 'Unnamed' });
      current = data.parent;
    }
    return chain.reverse(); // root → current
  }, []);

  // Read recent files from files/library/files and include breadcrumb path
  const fetchRecentFilesWithBreadcrumbs = useCallback(async () => {
    try {
      const colRef = collection(db, 'files', 'library', 'files');
      const querySnap = await getDocs(colRef);

      const filesWithCrumbs = await Promise.all(
        querySnap.docs.map(async (d) => {
          const f = d.data();
          const folderId = f.folder_id || f.child_id || f.parent_id || null;
          const breadcrumb = folderId ? await fetchBreadcrumb(folderId) : [];
          const libraryPath = breadcrumb.map((b) => b.name).join(' / ');

          return {
            id: d.id,
            ...f,
            libraryPath,
          };
        })
      );

      // sort newest first
      filesWithCrumbs.sort((a, b) => {
        const da = new Date(a.upload_date || 0).getTime();
        const dbt = new Date(b.upload_date || 0).getTime();
        return dbt - da;
      });

      setRecentFiles(filesWithCrumbs);
    } catch (err) {
      console.error('Error fetching recent files:', err);
    }
  }, [fetchBreadcrumb]);

  useEffect(() => {
    fetchRecentFilesWithBreadcrumbs();
  }, [fetchRecentFilesWithBreadcrumbs]);

  // Build libraries with counts (direct + total)
  const fetchLibraries = useCallback(async () => {
    setLoading(true);
    try {
      // libraries
      const libsSnap = await getDocs(collection(db, 'library'));
      const libraryList = libsSnap.docs.map((d) => {
        const data = d.data();
        // parent can be: undefined | string | DocumentReference-like object
        const parentRaw = data.parent;
        let parentId = null;
        if (typeof parentRaw === 'string') {
          parentId = parentRaw;
        } else if (parentRaw && typeof parentRaw === 'object') {
          // Try common shapes for DocumentReference
          // 1) Firestore v9 reference: parentRaw.id
          // 2) Fallback: extract last segment from a path, if present
          parentId =
            parentRaw.id ||
            parentRaw._key?.path?.segments?.slice(-1)[0] ||
            parentRaw.path?.split('/')?.slice(-1)[0] ||
            null;
        }
        return {
          id: d.id,
          library_name: data.library_name || 'Unnamed',
          parent: parentId, // <-- always a string or null now
          date_created: data.date_created || null,
          date_updated: data.date_updated || null,
          fileCount: 0,
          totalFileCount: 0,
        };
      });

      if (libraryList.length === 0) {
        setLibraries([]);
        return;
      }

      // files
      const filesSnap = await getDocs(
        collection(db, 'files', 'library', 'files')
      );
      const files = filesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // folder -> direct count
      const directCountByFolder = new Map();
      for (const f of files) {
        const folderId = f.folder_id;
        if (!folderId) continue;
        directCountByFolder.set(
          folderId,
          (directCountByFolder.get(folderId) || 0) + 1
        );
      }

      // parent -> children
      const childrenByParent = new Map();
      for (const lib of libraryList) {
        if (lib.parent) {
          if (!childrenByParent.has(lib.parent))
            childrenByParent.set(lib.parent, []);
          childrenByParent.get(lib.parent).push(lib.id);
        }
      }

      // total count (recursive)
      const memo = new Map();
      const totalFor = (libId) => {
        if (memo.has(libId)) return memo.get(libId);
        const self = directCountByFolder.get(libId) || 0;
        const kids = childrenByParent.get(libId) || [];
        const total = kids.reduce(
          (sum, childId) => sum + totalFor(childId),
          self
        );
        memo.set(libId, total);
        return total;
      };

      const updated = libraryList
        .map((lib) => ({
          ...lib,
          fileCount: directCountByFolder.get(lib.id) || 0,
          totalFileCount: totalFor(lib.id),
        }))
        .sort((a, b) =>
          (a.library_name || '').localeCompare(b.library_name || '')
        );

      setLibraries(updated);
    } catch (err) {
      console.error('Error fetching libraries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLibraries();
  }, [fetchLibraries]);

  // Create root library
  const handleCreateLibrary = async () => {
    const name = newLibraryName.trim();
    if (!name) {
      toast.error('Enter a Folder Name');
      return;
    }
    try {
      const now = new Date().toISOString();
      const newLib = { library_name: name, date_created: now };
      const docRef = await addDoc(collection(db, 'library'), newLib);
      setLibraries((prev) => [...prev, { id: docRef.id, ...newLib }]);
      setNewLibraryName('');
      toast.success(`${name} has been created`);
    } catch (error) {
      console.error('Error creating library:', error);
      toast.error('Failed to create folder');
    }
  };

  // Create child library
  const handleCreateSubLibrary = async (parentLibraryId) => {
    const name = newSubLibraryName.trim();
    if (!name) {
      toast.error('Enter a Folder Name');
      return;
    }
    try {
      const now = new Date().toISOString();
      const newSub = {
        library_name: name,
        parent: parentLibraryId,
        date_created: now,
      };
      const docRef = await addDoc(collection(db, 'library'), newSub);
      setLibraries((prev) => [...prev, { id: docRef.id, ...newSub }]);
      setNewSubLibraryName('');
      toast.success(`${name} has been created`);
    } catch (error) {
      console.error('Error creating sub-library:', error);
      toast.error('Error Creating Sub-Folder');
    }
  };

  // Collect all descendants of a folder (ids)
  const fetchAllDescendantFolders = async (parentId, libs) => {
    const childFolders = libs.filter((lib) => lib.parent === parentId);
    const ids = [parentId];
    for (const f of childFolders) {
      const childIds = await fetchAllDescendantFolders(f.id, libs);
      ids.push(...childIds);
    }
    return ids;
  };

  // Delete all files for a folder id
  const deleteFolderFiles = async (folderId) => {
    try {
      const filesRef = collection(db, 'files', 'library', 'files');
      const snapshot = await getDocs(
        query(filesRef, where('folder_id', '==', folderId))
      );

      for (const fileDoc of snapshot.docs) {
        const fileData = fileDoc.data();
        const fileId = fileDoc.id;

        // delete file in Storage
        const storagePath = `library/${fileId}/${fileData.file_name}`;
        await deleteObject(deleteRef(storage, storagePath)).catch((e) =>
          console.warn(
            'Storage delete failed (continuing):',
            storagePath,
            e?.message
          )
        );

        // delete Firestore doc
        await deleteDoc(doc(db, 'files', 'library', 'files', fileId));
      }
    } catch (error) {
      console.error(`Error deleting files for folder ${folderId}:`, error);
    }
  };

  const deleteFolderAndDescendants = async (folderIds) => {
    try {
      for (const folderId of folderIds) {
        await deleteDoc(doc(db, 'library', folderId));
        await deleteFolderFiles(folderId);
      }
    } catch (error) {
      console.error('Error deleting folders and descendants:', error);
    }
  };

  const handleDeleteLibrary = async (parentId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Deleting this folder will also delete all subfolders and files!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!result.isConfirmed) return;

    try {
      const libsSnap = await getDocs(collection(db, 'library'));
      const libs = libsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const ids = await fetchAllDescendantFolders(parentId, libs);

      await deleteFolderAndDescendants(ids);

      setLibraries((prev) => prev.filter((l) => !ids.includes(l.id)));
      toast.success('The folder and its contents have been deleted.');
      // refresh recent files list as well
      fetchRecentFilesWithBreadcrumbs();
    } catch (error) {
      console.error('Error deleting library:', error);
      Swal.fire(
        'Error!',
        'Failed to delete the folder and its contents.',
        'error'
      );
    }
  };

  // Reset modal form
  const resetForm = () => {
    setSelectedParent('');
    setSelectedChild('');
    setFileTitle('');
    setDocReviewDate('');
    setDocDeleteDate('');
    setTags([]);
  };

  // Upload + save Firestore metadata
  const formValidation = async () => {
    const selected = docUploaderRef.current?.getSelectedFile();

    if (!selectedParent) {
      toast.error('Please select a Parent Library');
      return false;
    }
    const hasChildren = getChildLibraries(selectedParent).length > 0;
    if (hasChildren && !selectedChild) {
      toast.error('Please select a Child Library');
      return false;
    }
    if (!fileTitle.trim()) {
      toast.error('Please enter a File Name');
      return false;
    }
    if (!docReviewDate) {
      toast.error('Please choose a Review Date');
      return false;
    }
    if (!docDeleteDate) {
      toast.error('Please choose a Deletion Date');
      return false;
    }
    if (!selected) {
      toast.error('Please choose a file to upload');
      return false;
    }

    setIsUploading(true);
    try {
      const fileId = uuidv4(); // use as both storage folder + Firestore doc id
      // Upload to Storage
      const { downloadURL, fileSizeInMB } =
        await docUploaderRef.current.handleUpload(
          `library/${fileId}/${selected.name}`
        );

      // Choose the folder id we file under: child if set, otherwise parent
      const folderId = selectedChild || selectedParent;

      // Save Firestore doc
      await setDoc(doc(db, 'files', 'library', 'files', fileId), {
        file_title: fileTitle,
        file_name: selected.name,
        file_url: downloadURL,
        file_size: fileSizeInMB,
        parent_id: selectedParent || null,
        child_id: selectedChild || null,
        folder_id: folderId || null, // <- important for browsing/breadcrumbs
        review_date: docReviewDate,
        delete_date: docDeleteDate,
        upload_date: new Date().toISOString(),
        uploaded_by: globalUser?.uid || null,
        tags: Array.isArray(tags) ? tags : [],
      });

      // bump counters on parent and child (if set)
      const now = new Date().toISOString();
      const parentRef = doc(db, 'library', selectedParent);
      await updateDoc(parentRef, {
        date_updated: now,
        libraryCount: increment(1),
      });

      if (selectedChild) {
        const childRef = doc(db, 'library', selectedChild);
        await updateDoc(childRef, {
          date_updated: now,
          libraryCount: increment(1),
        });
      }

      // refresh lists
      await fetchRecentFilesWithBreadcrumbs();
      await fetchLibraries();

      // clean up
      resetForm();
      toast.success('File uploaded successfully');
      return true;
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  // pagination
  const indexOfLastFile = currentPage * filesPerPage;
  const currentFiles = recentFiles.slice(
    indexOfLastFile - filesPerPage,
    indexOfLastFile
  );
  const totalPages = Math.max(1, Math.ceil(recentFiles.length / filesPerPage));

  // Delete a single file
  const handleDeleteFile = async (file) => {
    if (!file?.id || !file?.file_name) {
      toast.error('Missing file details. Cannot delete.');
      return;
    }
    try {
      // delete firestore doc
      await deleteDoc(doc(db, 'files', 'library', 'files', file.id));

      // delete storage object
      const storagePath = `library/${file.id}/${file.file_name}`;
      await deleteObject(deleteRef(storage, storagePath));

      // decrement libraryCount on parent/child if they exist
      for (const libId of [file.parent_id, file.child_id]) {
        if (!libId) continue;
        const ref = doc(db, 'library', libId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const current = snap.data().libraryCount || 0;
          await updateDoc(ref, { libraryCount: Math.max(current - 1, 0) });
        }
      }

      // update UI
      setRecentFiles((prev) => prev.filter((x) => x.id !== file.id));
      toast.success('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Could not delete the file');
    }
  };

  return (
    <Layout isAdminPage={true}>
      <div className='row page-titles'>
        <div className='col-lg-3'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Administration</li>
            <li className='breadcrumb-item'>Library</li>
          </ol>
        </div>
        <div className='col-lg-4'>
          <Tour steps={libraryAdminTourSteps} pageKey='library-admin' />
        </div>
      </div>

      {/* Sidebar */}
      <div className='col-xl-12 col-lg-4'>
        <div className='row'>
          <div className='col-xl-4 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent'>
                <h5 className='mb-0'>Library Folders</h5>
              </div>

              <div className='p-4 border-top'>
                <div className='custom-accordion'>
                  {/* Create Library Section */}
                  <div className='d-flex align-items-center'>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Create Root Library'
                        value={newLibraryName}
                        onChange={(e) => setNewLibraryName(e.target.value)}
                      />
                      <button
                        className='btn btn-primary btn-xs'
                        type='button'
                        onClick={handleCreateLibrary}
                      >
                        Create
                      </button>
                    </div>
                  </div>

                  <hr />

                  {/* Parent Library Folder */}
                  {libraries
                    .filter((library) => !library.parent)
                    .map((library) => (
                      <div key={library.id} className='libraryAccordion mb-2'>
                        <a
                          className='text-body fw-medium py-1 d-flex align-items-center'
                          data-bs-toggle='collapse'
                          href={`#categories-collapse-${library.id}`}
                          role='button'
                          aria-expanded={activeAccordion === library.id}
                          aria-controls={`categories-collapse-${library.id}`}
                          onClick={() => toggleAccordion(library.id)}
                        >
                          <i
                            className={`mdi ${
                              activeAccordion === library.id
                                ? 'mdi-folder-open'
                                : 'mdi-folder'
                            } font-size-20 text-warning me-2`}
                          />
                          {library.library_name}
                          <i
                            className={`mdi mdi-chevron-${
                              activeAccordion === library.id ? 'down' : 'up'
                            } accor-down-icon ms-auto`}
                          />
                          {/* Parent Delete */}
                          <button
                            className='deleteLibrary btn btn-link p-0 ms-2'
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLibrary(library.id);
                            }}
                          >
                            <i
                              className='fa fa-trash font-12 align-middle me-2'
                              style={{ color: '#cccccc' }}
                            />
                          </button>
                          {/* Parent Count */}
                          <span
                            className={`badge badge-xs ${
                              library.totalFileCount > 0
                                ? 'badge-primary'
                                : 'badge-light'
                            } text-white badge-sm float-end`}
                          >
                            {library.totalFileCount || 0}
                          </span>
                        </a>

                        {/* Child Folders */}
                        <div
                          className={`collapse ${
                            activeAccordion === library.id ? 'show' : ''
                          }`}
                          id={`categories-collapse-${library.id}`}
                        >
                          <div className='card border-0 shadow-none ps-2 mb-0'>
                            <ul className='list-unstyled mb-1'>
                              {getChildLibraries(library.id).length > 0 ? (
                                getChildLibraries(library.id).map(
                                  (subLibrary) => (
                                    <li
                                      key={subLibrary.id}
                                      className='d-flex align-items-center justify-content-between'
                                    >
                                      <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2' />
                                      <span
                                        onClick={() =>
                                          handleLibraryClick(
                                            subLibrary.id,
                                            subLibrary.library_name
                                          )
                                        }
                                        className='text-body cursor-pointer'
                                      >
                                        {subLibrary.library_name}
                                      </span>
                                      {/* File Couunt */}
                                      {subLibrary.totalFileCount > 0 && (
                                        <span className='text-primary fst-italic small ms-1'>
                                          - {subLibrary.totalFileCount}{' '}
                                          {subLibrary.totalFileCount > 1
                                            ? 'files'
                                            : 'file'}
                                        </span>
                                      )}
                                      {/* Delete Sub Folder */}
                                      <button
                                        className='btn btn-link p-0 ms-auto'
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteLibrary(subLibrary.id);
                                        }}
                                      >
                                        <i
                                          className='fa fa-trash font-12 align-middle me-2'
                                          style={{ color: '#cccccc' }}
                                        />
                                      </button>
                                    </li>
                                  )
                                )
                              ) : (
                                <li>
                                  <div className='d-flex align-items-center'>
                                    <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2' />
                                    <small className='text-muted'>
                                      No Subfolders
                                    </small>
                                  </div>
                                </li>
                              )}
                            </ul>

                            {/* Sub-library creation input */}
                            <div className='d-flex align-items-center mt-2'>
                              <div className='col-lg-8'>
                                <div className='input-group'>
                                  <input
                                    type='text'
                                    className='form-control'
                                    placeholder='Create Sub Library'
                                    value={newSubLibraryName}
                                    onChange={(e) =>
                                      setNewSubLibraryName(e.target.value)
                                    }
                                  />
                                  <button
                                    className='btn btn-warning btn-xs'
                                    type='button'
                                    onClick={() =>
                                      handleCreateSubLibrary(library.id)
                                    }
                                  >
                                    Create
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Document Listing */}
          <div className='col-xl-8 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent'>
                <h5 className='mb-0'>Recent Files</h5>
                <button
                  type='button'
                  className='btn btn-primary btn-xs'
                  data-bs-toggle='modal'
                  data-bs-target='#uploadFile'
                >
                  <i className='fa fa-upload font-12 align-middle me-2' />{' '}
                  Upload a File
                </button>
              </div>

              {/* Recent files table */}
              <div className='card-body position-relative'>
                <table className='table table-hover table-responsive-sm'>
                  <thead>
                    <tr>
                      <th className='fs-12 fw-bold'>#</th>
                      <th className='fs-12 fw-bold'>Library</th>
                      <th className='fs-12 fw-bold'>File Name</th>
                      <th className='fs-12 fw-bold'>Size</th>
                      <th className='fs-12 fw-bold'>Uploaded On</th>
                      <th className='fs-12 fw-bold'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentFiles.map((file, index) => (
                      <tr key={file.id}>
                        <th>
                          <span className='badge badge-light'>
                            {(currentPage - 1) * filesPerPage + index + 1}
                          </span>
                        </th>
                        <td
                          className='text-truncate'
                          title={file.libraryPath || ''}
                        >
                          {file.libraryPath || '—'}
                        </td>
                        <td>
                          <a
                            href={file.file_url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-dark fw-medium'
                          >
                            <ReadMoreTextBase
                              text={file.file_title}
                              wordLimit={5}
                              readMore={false}
                              plainText={true}
                            />
                          </a>
                        </td>
                        <td>{file.file_size ? `${file.file_size} MB` : '—'}</td>
                        <td className='color-primary'>
                          {file.upload_date
                            ? formatShortDate(file.upload_date)
                            : 'Unknown'}
                        </td>
                        <td>
                          <button
                            className='btn btn-link p-0 ms-auto'
                            onClick={(e) => {
                              e.preventDefault();
                              setDeleteFileName(file.file_title);
                              setDeleteFileObj(file);
                            }}
                            data-bs-toggle='modal'
                            data-bs-target='#confirmFileDelete'
                          >
                            <i
                              className='fa fa-trash font-12 align-middle me-2'
                              style={{ color: '#cccccc' }}
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <ul className='pagination pagination-sm pagination-circle'>
                  {/* Prev */}
                  <li
                    className={`page-item page-indicator ${
                      currentPage === 1 ? 'disabled' : ''
                    }`}
                  >
                    <button
                      type='button'
                      className='page-link'
                      aria-label='Previous page'
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <i className='la la-angle-left' />
                    </button>
                  </li>

                  {/* Page numbers + ellipses */}
                  {getPageNumbers(currentPage, totalPages).map((p, idx) =>
                    p === '…' ? (
                      <li
                        key={`ellipsis-${idx}`}
                        className='page-item disabled'
                        aria-hidden='true'
                      >
                        <span className='page-link'>…</span>
                      </li>
                    ) : (
                      <li
                        key={`page-${p}`}
                        className={`page-item ${
                          p === currentPage ? 'active' : ''
                        }`}
                      >
                        <button
                          type='button'
                          className='page-link'
                          aria-current={p === currentPage ? 'page' : undefined}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                      </li>
                    )
                  )}

                  {/* Next */}
                  <li
                    className={`page-item page-indicator ${
                      currentPage === totalPages ? 'disabled' : ''
                    }`}
                  >
                    <button
                      type='button'
                      className='page-link'
                      aria-label='Next page'
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    >
                      <i className='la la-angle-right' />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          {/* end listing */}
        </div>
      </div>

      {/* File Upload Modal */}
      <Modal
        id='uploadFile'
        title='Add a New Library File'
        confirmText='Upload'
        loading={isSaving || isUploading}
        loadingText='Uploading...'
        onConfirm={async () => {
          setIsSaving(true);
          try {
            const ok = await formValidation();
            if (!ok) return;
          } finally {
            setIsSaving(false);
          }
        }}
      >
        {/* Parent library */}
        <div className='col-12 form-floating mb-3'>
          <select
            id='parentLibrary'
            className='form-select formParent native-select'
            value={selectedParent}
            onChange={(e) => {
              const nextParent = e.target.value;
              setSelectedParent(nextParent);
              setSelectedChild('');
            }}
            required
            aria-describedby='parentHelp'
          >
            <option value=''></option>
            {libraries
              .filter((lib) => !lib.parent)
              .map((lib) => (
                <option key={lib.id} value={lib.id}>
                  {lib.library_name}
                </option>
              ))}
          </select>
          <div id='parentHelp' className='form-text'>
            Choose the top-level library.
          </div>
        </div>

        {/* Child library (only when there are children) */}
        {selectedParent && getChildLibraries(selectedParent).length > 0 && (
          <div className='col-12 form-floating mb-3'>
            <select
              id='childLibrary'
              className='form-select'
              value={selectedChild}
              onChange={(e) => setSelectedChild(e.target.value)}
              aria-describedby='childHelp'
            >
              <option value=''></option>
              {getChildLibraries(selectedParent).map((lib) => (
                <option key={lib.id} value={lib.id}>
                  {lib.library_name}
                </option>
              ))}
            </select>
            <div id='childHelp' className='form-text'>
              Narrow to a sub-folder if needed.
            </div>
          </div>
        )}

        {/* File Name */}
        <div className='col-12 form-floating mb-3'>
          <input
            id='fileName'
            className='form-control formFileName'
            type='text'
            value={fileTitle}
            onChange={(e) => setFileTitle(e.target.value)}
            placeholder=' '
            required
            minLength={1}
            aria-describedby='fileNameHelp'
          />
          <div id='fileNameHelp' className='form-text'>
            Example: Policy-Template.pdf
          </div>
        </div>

        {/* Doc Review Date */}
        <div className='col-12 form-floating mb-3'>
          <input
            id='docReviewDate'
            className='form-control formReview'
            type='date'
            value={docReviewDate}
            onChange={(e) => setDocReviewDate(e.target.value)}
            placeholder=' '
            aria-describedby='reviewHelp'
          />
          <div id='reviewHelp' className='form-text'>
            When should we review this document?
          </div>
        </div>

        {/* Doc Deletion Date */}
        <div className='col-12 form-floating mb-3'>
          <input
            id='docDeleteDate'
            className='form-control formDelete'
            type='date'
            value={docDeleteDate}
            onChange={(e) => setDocDeleteDate(e.target.value)}
            placeholder=' '
            aria-describedby='deleteHelp'
            min={docReviewDate || undefined}
          />
          <div id='deleteHelp' className='form-text'>
            Set a retention end date if required.
          </div>
        </div>

        {/* Tags */}
        <div className='col-12 form-floating mb-3 formTags'>
          <TagInput id='docTags' tags={tags} onChange={setTags} />
        </div>

        {/* Uploader */}
        <DocUploader ref={docUploaderRef} uid={globalUser?.uid} />
      </Modal>

      {/* Confirm Delete Dog File*/}
      <Modal
        id='confirmFileDelete'
        title='Remove This File?'
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await handleDeleteFile(deleteFileObj);
          } finally {
            setIsDeleting(false);
          }
        }}
        confirmText='Yes, delete'
        cancelText='Cancel'
        loading={isDeleting}
        loadingText='Deleting...'
        size='medium'
      >
        <p>
          Are you sure you want to delete <strong>{deleteFileName}</strong>?
        </p>
      </Modal>
    </Layout>
  );
}

export default LibraryAdmin;
